import { useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import * as R from 'ramda';

import { retrieveInventorySchemaTreeNode } from '@atom/actions/inventorySchemaActions';
import { Checkbox, Progress, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import iconUtilities from '@atom/utilities/iconUtilities';

import classes from './elements.module.css';

const RIGHT_ARROW = 'right-arrow';
const DOWN_ARROW = 'down-arrow';
const VISIBLE = 'inherit';
const HIDDEN = 'none';

interface Element {
  id: string;
  name: string;
  elementGroups: any[];
  hasChildren: boolean;
  assetType: string;
  markerId?: number;
}

interface Props {
  element: Element;
  isChecked?: boolean;
  onClick: (id: string) => void;
  value?: number;
  schemaQuantities: object;
  selectedItems: Set<string>;
  elementPath?: any[];
  loading?: boolean;
  parentId?: string;
  elementExistsOnAsset?: boolean;
}

const styles = {
  rowStyle: {
    paddingLeft: '25px',
  },
  imageStyle: {
    marginRight: '10px',
  },
  fhwaStyle: {
    color: colors.neutral.gray,
    marginRight: '10px',
  },
};

const FhwaSchemaElementListRow = ({
  element,
  elementPath,
  onClick,
  isChecked,
  value,
  loading,
  selectedItems,
  schemaQuantities,
  parentId,
  elementExistsOnAsset,
}: Props) => {
  const dispatch = useDispatch();
  const [icon, setIcon] = useState(iconUtilities.leftIcon(RIGHT_ARROW));
  const [expanded, setExpanded] = useState(HIDDEN);

  const retrieveTreeNode = () => {
    if (!element.elementGroups) {
      const data = {
        id: R.last(element.id.split('.')),
        elementPath,
      };
      dispatch(retrieveInventorySchemaTreeNode(data));
    }
    return false;
  };

  const toggleSection = () => {
    if (expanded === HIDDEN) {
      setIcon(iconUtilities.leftIcon(DOWN_ARROW));
      setExpanded(VISIBLE);
      retrieveTreeNode();
    } else {
      setIcon(iconUtilities.leftIcon(RIGHT_ARROW));
      setExpanded(HIDDEN);
    }
  };

  const isDisabled =
    (parentId && !selectedItems.has(parentId)) || elementExistsOnAsset;

  const leftIcon = iconUtilities.buildElementIcon(
    element.markerId,
    styles,
    false,
  );

  return (
    <div style={styles.rowStyle}>
      <div
        className={clsx(
          classes.elementListRow,
          isDisabled && classes.disabled,
          classes.modal,
        )}
      >
        {element.hasChildren && (
          <span className={classes.schemaElementIcon} onClick={toggleSection}>
            {loading ? <Progress thickness={2} size={16} /> : icon}
          </span>
        )}
        <div>
          <Checkbox
            checked={isChecked}
            onChange={() => onClick(element.id)}
            disabled={isDisabled}
          />
        </div>
        <div
          className={classes.elementLabel}
          onClick={!isDisabled ? () => onClick(element.id) : () => {}}
        >
          {leftIcon}
          {element.name}
        </div>
        <div>
          <TextField
            type="number"
            name={element.id}
            value={value || 1}
            disabled
            slotProps={{ input: { style: { textAlign: 'center' } } }}
          />
        </div>
      </div>
      <div
        className={classes.subElementListContainer}
        style={{ display: expanded }}
      >
        {element.elementGroups &&
          element.elementGroups.map((elementGroup, elementGroupIndex) =>
            elementGroup.elements.map((subElement, subElementIndex) => {
              const subElementWithPathId = {
                ...subElement,
                id: `${element.id}.${subElement.id}`,
              };

              return (
                <FhwaSchemaElementListRow
                  isChecked={selectedItems.has(subElementWithPathId.id)}
                  schemaQuantities={schemaQuantities}
                  selectedItems={selectedItems}
                  key={subElementWithPathId.id}
                  element={subElementWithPathId}
                  onClick={onClick}
                  loading={loading}
                  value={schemaQuantities[subElementWithPathId.id]}
                  parentId={element.id}
                  elementPath={[
                    ...elementPath,
                    'elementGroups',
                    elementGroupIndex,
                    'elements',
                    subElementIndex,
                  ]}
                />
              );
            }),
          )}
      </div>
    </div>
  );
};

export default FhwaSchemaElementListRow;
